/**
 * Which devices can split a cable, and which of their ports a split uses.
 *
 * An output feeds one cable. To get one signal to two places you put a box in
 * between: an HDMI splitter, an SDI distribution amp, a power strip, a switch.
 * Rather than keep a list of those by name, a splitter is anything with an
 * input and at least two outputs of the cable's signal, so new gear in `data/`
 * turns up in the picker on its own.
 *
 * Pure, so the choice is testable without a browser or a database.
 */
import type { DeviceCategory, DeviceDTO, DeviceOption, PortDTO, SignalCategory } from '@routr/shared'

/** The two ends of the cable being split, as far as choosing a box goes. */
export type SplitEnds = {
  from: Pick<PortDTO, 'signal' | 'connector'>
  to: Pick<PortDTO, 'connector'>
}

export type SplitterOption = {
  device: DeviceOption
  /** Inputs and outputs carrying the cable's signal. */
  inputs: number
  outputs: number
  /** Outputs with the destination's plug: what it can feed without an adapter. */
  matchingOutputs: number
  /**
   * A true splitter for this cable: takes its plug in and has two or more of
   * the destination's plug out. A converter with one HDMI loop and one SDI
   * out carries the signal twice, but only one of those fits an HDMI input.
   */
  sameConnector: boolean
}

/**
 * Boxes whose job is distribution come before boxes that can merely do it: a
 * distribution amp before a 40-input switcher with a spare aux. The catalogue
 * names its distribution amps, splitters and PDUs as such, which tells them
 * apart from a scaler that happens to have three HDMI outputs; failing that,
 * the category is the next-best sign.
 */
const DEDICATED = /distribut|splitter|\bpdu\b/i
const DISTRIBUTION_CATEGORIES: ReadonlySet<DeviceCategory> = new Set(['CONVERTER', 'POWER', 'NETWORK', 'AUDIO'])

/** Ports a split can use: onboard (not a card in a slot), of this signal and direction. */
function portsOf(device: DeviceDTO, signal: SignalCategory, direction: PortDTO['direction']): PortDTO[] {
  return device.ports
    .filter((port) => port.slotId === null && port.signal === signal && port.direction === direction)
    .sort((a, b) => a.position - b.position)
}

/**
 * Every device that can split a cable running `from` → `to`, best first: true
 * splitters for these plugs, then gear named for distribution, then
 * distribution categories, then the smallest box that does the job, then by
 * name.
 */
export function findSplitters(devices: readonly DeviceDTO[], { from, to }: SplitEnds): SplitterOption[] {
  const options: Array<SplitterOption & { dedicated: boolean; distribution: boolean }> = []
  for (const device of devices) {
    const inputs = portsOf(device, from.signal, 'INPUT')
    const outputs = portsOf(device, from.signal, 'OUTPUT')
    if (inputs.length === 0 || outputs.length < 2) continue
    const matchingOutputs = outputs.filter((port) => port.connector === to.connector).length
    options.push({
      device: {
        id: device.id,
        workspaceId: device.workspaceId,
        name: device.name,
        manufacturer: device.manufacturer,
        model: device.model,
        category: device.category,
      },
      inputs: inputs.length,
      outputs: outputs.length,
      matchingOutputs,
      sameConnector: inputs.some((port) => port.connector === from.connector) && matchingOutputs >= 2,
      dedicated: DEDICATED.test(`${device.name} ${device.model}`),
      distribution: DISTRIBUTION_CATEGORIES.has(device.category),
    })
  }
  options.sort(
    (a, b) =>
      Number(b.sameConnector) - Number(a.sameConnector) ||
      Number(b.dedicated) - Number(a.dedicated) ||
      Number(b.distribution) - Number(a.distribution) ||
      (a.sameConnector ? a.matchingOutputs : a.outputs) - (b.sameConnector ? b.matchingOutputs : b.outputs) ||
      `${a.device.manufacturer} ${a.device.model}`.localeCompare(`${b.device.manufacturer} ${b.device.model}`),
  )
  return options.map(({ dedicated: _dedicated, distribution: _distribution, ...option }) => option)
}

/**
 * The splitter's input the cable now runs into, and the outputs that feed on:
 * one for the original destination, two when a second destination is fed
 * too. Matching plugs first, then in the order the box lists them. Null when
 * the device cannot do it.
 */
export function splitterPorts(
  device: DeviceDTO,
  { from, to }: SplitEnds,
  feeds: 1 | 2,
): { input: PortDTO; outputs: PortDTO[] } | null {
  const byPlug = (connector: string) => (a: PortDTO, b: PortDTO) =>
    Number(b.connector === connector) - Number(a.connector === connector) || a.position - b.position
  const [input] = portsOf(device, from.signal, 'INPUT').sort(byPlug(from.connector))
  const outputs = portsOf(device, from.signal, 'OUTPUT').sort(byPlug(to.connector)).slice(0, feeds)
  if (!input || outputs.length < feeds) return null
  return { input, outputs }
}
